import { gql } from '@apollo/client';
import { apolloClient } from '../apollo/client';

export const ATLETA_POR_ID = gql`
  query AtletaPorId($id: ID!) {
    atletaPorId(id: $id) {
      id
      cpf
      telefone
      genero
      tamanhoCamiseta
      tamanhoSapato
      sobre
      cidade {
        id
        nome
        uf
        estado {
          id
          nome
          uf
        }
      }
      dataNascimento
      usuario {
        id
        nome
        email
        imagemUrl
        bannerUrl
        username
        role
        ativo
      }
    }
  }
`;

export interface AtletaData {
  atletaPorId: {
    id: string;
    telefone: string;
    genero?: string | null;
    tamanhoCamiseta?: string | null;
    tamanhoSapato?: number | null;
    sobre?: string;
    cpf: string;
    cidade: {
      id: string;
      nome: string;
      uf: string;
      estado: {
        id: string;
        nome: string;
        uf: string;
      };
    };
    dataNascimento: string;
    usuario: {
      id: string;
      nome: string;
      email: string;
      imagemUrl: string;
      bannerUrl?: string | null;
      username: string;
      role?: string;
      ativo?: Boolean;
    };
  };
}

export interface AtletaVars {
  id: string;
}

export interface AtualizarAtletaInput {
  cpf?: string | null;
  dataNascimento?: string | null;
  telefone?: string | null;
  cidadeId?: string | null;
  sobre?: string | null;
}

export const buscarAtletaPorId = async (id: string): Promise<Atleta> => {
  const { data } = await apolloClient.query<{ atletaPorId: Atleta }, AtletaVars>({
    query: ATLETA_POR_ID,
    variables: { id },
    fetchPolicy: 'no-cache',
  });
  return data.atletaPorId;
};

export const ATUALIZAR_ATLETA = gql`
  mutation AtualizarAtleta(
    $id: ID!
    $cidadeId: ID
    $dataNascimento: String
    $sobre: String
    $telefone: String
    $genero: String
    $tamanhoCamiseta: TamanhoCamiseta
    $tamanhoSapato: Int
    $cpf: String
  ) {
    atualizarAtleta(
      id: $id
      input: {
        cidadeId: $cidadeId
        dataNascimento: $dataNascimento
        sobre: $sobre
        telefone: $telefone
        genero: $genero
        tamanhoCamiseta: $tamanhoCamiseta
        tamanhoSapato: $tamanhoSapato
        cpf: $cpf
      }
    ) {
      id
    }
  }
`;

export const atualizarAtleta = async (
  id: string,
  input: AtualizarAtletaInput,
): Promise<Atleta> => {
  const { data } = await apolloClient.mutate<{ atualizarAtleta: Atleta }>({
    mutation: ATUALIZAR_ATLETA,
    variables: {
      id,
      cidadeId: input.cidadeId || undefined,
      dataNascimento: input.dataNascimento || undefined,
      telefone: input.telefone || undefined,
      sobre: input.sobre ?? undefined,
      cpf: input.cpf || undefined,
    },
  });
  return data!.atualizarAtleta;
};


export const ATLETAS_POR_NOME = gql`
  query AtletasPorNome($nome: String!) {
    atletasPorNome(nome: $nome) {
      id
      usuario {
        id
        email
        nome
        imagemUrl
        username
      }
      cidade {
        id
        nome
        uf
      }
    }
  }
`;

export interface BuscarAtletasData {
  atletasPorNome: {
    id: string;
    usuario: {
      id: string;
      email: string;
      nome: string;
      imagemUrl?: string | null;
      username?: string | null;
    };
    cidade: {
      id: string;
      nome: string;
      uf: string;
    };
  }[];
}

export interface BuscarAtletasVars {
  nome: string;
}

export const buscarAtletas = async (nome: string) => {
  const { data } = await apolloClient.query<BuscarAtletasData, BuscarAtletasVars>({
    query: ATLETAS_POR_NOME,
    variables: { nome: nome?.trim() ?? '' },
    fetchPolicy: 'no-cache',
  });
  return data.atletasPorNome;
};

export const ATLETAS_POR_USERNAME = gql`
  query AtletasPorUsername($username: String!, $categoriaTorneioId: ID) {
    atletasPorUsername(username: $username, categoriaTorneioId: $categoriaTorneioId) {
      id
      usuario {
        id
        email
        nome
        imagemUrl
        username
      }
      cidade {
        id
        nome
        uf
      }
    }
  }
`;

export interface BuscarAtletasPorUsernameData {
  atletasPorUsername: {
    id: string;
    usuario: {
      id: string;
      email: string;
      nome: string;
      imagemUrl?: string | null;
      username: string;
    };
    cidade: {
      id: string;
      nome: string;
      uf: string;
    };
  }[];
}

export interface BuscarAtletasPorUsernameVars {
  username: string;
  categoriaTorneioId?: string;
}

export const buscarAtletasPorUsername = async (username: string, categoriaTorneioId?: string) => {
  const { data } = await apolloClient.query<BuscarAtletasPorUsernameData, BuscarAtletasPorUsernameVars>({
    query: ATLETAS_POR_USERNAME,
    variables: { username: username?.trim() ?? '', categoriaTorneioId },
    fetchPolicy: 'no-cache',
  });
  return data.atletasPorUsername;
};

export const ATLETAS_FILTRADOS = gql`
  query AtletasFiltrados(
    $cidadeId: ID
    $direction: String
    $genero: String
    $nome: String
    $page: Int
    $size: Int
    $sortBy: String
    $ativo: Boolean
    $cpf: String
  ) {
    atletasFiltrados(
      ativo: $ativo
      cidadeId: $cidadeId
      direction: $direction
      genero: $genero
      nome: $nome
      cpf: $cpf
      page: $page
      size: $size
      sortBy: $sortBy
    ) {
      page
      size
      totalItems
      totalPages
      items {
        atualizadoEm
        cpf
        criadoEm
        dataNascimento
        genero
        id
        seguidorId
        sobre
        tamanhoCamiseta
        tamanhoSapato
        telefone
        cidade {
          id
          nome
          uf
        }
        usuario {
          ativo
          atualizadoEm
          bannerUrl
          criadoEm
          email
          id
          imagemUrl
          nome
          role
          username
        }
      }
    }
  }
`;

export interface Atleta {
  atualizadoEm?: string;
  cpf?: string | null;
  criadoEm?: string;
  dataNascimento?: string | null;
  genero?: string | null;
  id: string;
  seguidorId?: string | null;
  sobre?: string | null;
  tamanhoCamiseta?: string | null;
  tamanhoSapato?: number | null;
  telefone?: string | null;
  usuario?: {
    ativo?: boolean;
    atualizadoEm?: string;
    bannerUrl?: string | null;
    criadoEm?: string;
    email: string;
    id: string;
    imagemUrl?: string | null;
    nome: string;
    role?: string;
    username: string;
  } | null;
  cidade?: {
    id: string;
    nome: string;
    uf: string;
    estado?: {
      id: string;
      nome: string;
      uf: string;
    } | null;
  } | null;
}

export interface ListarAtletasData {
  atletasFiltrados: {
    items: Atleta[];
    page: number;
    size: number;
    totalItems: number;
    totalPages: number;
  };
}

export interface ListarAtletasVars {
  cidadeId?: string;
  direction?: string;
  genero?: string;
  nome?: string;
  page?: number;
  size?: number;
  sortBy?: string;
  ativo?: boolean;
  cpf?: string;
}

export async function listarAtletas(params?: {
  page?: number;
  size?: number;
  nomeUsuario?: string | null;
  cpf?: string | null;
  cidadeId?: string | null;
  ativo?: boolean | null;
}): Promise<ListarAtletasData['atletasFiltrados']> {
  const page = params?.page ?? 0;
  const size = params?.size ?? 10;
  const nome = (params?.nomeUsuario || '')?.trim() || undefined;
  const cpf = (params?.cpf || '')?.replace(/\D+/g, '') || undefined;
  const cidadeId = (params?.cidadeId || '')?.trim() || undefined;
  const ativo = typeof params?.ativo === 'boolean' ? params.ativo : undefined;

  const variables: ListarAtletasVars = {
    page,
    size,
    nome,
    cpf,
    cidadeId,
  };
  if (typeof ativo === 'boolean') {
    variables.ativo = ativo;
  }

  const { data } = await apolloClient.query<ListarAtletasData, ListarAtletasVars>({
    query: ATLETAS_FILTRADOS,
    variables,
    fetchPolicy: 'no-cache',
  });
  return data.atletasFiltrados;
}

export const DELETAR_ATLETA = gql`
  mutation DeletarAtleta($id: ID!) {
    deletarAtleta(id: $id)
  }
`;

export const deletarAtleta = async (id: string): Promise<void> => {
  await apolloClient.mutate({
    mutation: DELETAR_ATLETA,
    variables: { id },
  });
};
